import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { NovelService, Novel } from '../../services/novelService';
import PageHeader from '../common/PageHeader';
import './NovelEditor.css';

interface NovelEditorProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const NovelEditor: React.FC<NovelEditorProps> = ({ user, onClose, onToggleSidebar }) => {
  const { novelId } = useParams<{ novelId: string }>();
  const navigate = useNavigate();

  const [novel, setNovel] = useState<Novel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [showAddChapter, setShowAddChapter] = useState(false);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [editingChapterId, setEditingChapterId] = useState<string | null>(null);
  const [editedChapterTitle, setEditedChapterTitle] = useState('');
  const [activeMenuChapterId, setActiveMenuChapterId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [sortMode, setSortMode] = useState<'timestamp' | 'title'>('timestamp');

  useEffect(() => {
    if (novelId) {
      loadNovel();
    }
  }, [novelId]);

  // Update document title when novel loads
  useEffect(() => {
    if (novel) {
      document.title = `${novel.title} - Novel Editor`;
    } else {
      document.title = 'Novel Editor';
    }

    // Cleanup: reset title when component unmounts
    return () => {
      document.title = 'Memory Land';
    };
  }, [novel]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (activeMenuChapterId !== null) {
        setActiveMenuChapterId(null);
      }
    };

    if (activeMenuChapterId !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [activeMenuChapterId]);

  const loadNovel = async () => {
    if (!novelId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await NovelService.getNovel(novelId);
      // Ensure chapters are sorted by order field
      if (data.chapters) {
        data.chapters = [...data.chapters].sort((a, b) => (a.order || 0) - (b.order || 0));
      }
      setNovel(data);
    } catch (err) {
      console.error('Error loading novel:', err);
      setError('Failed to load novel');
    } finally {
      setLoading(false);
    }
  };

  const handleTitleEdit = () => {
    if (novel) {
      setEditedTitle(novel.title);
      setIsEditingTitle(true);
    }
  };

  const handleTitleSave = async () => {
    if (!novel || !novelId || !editedTitle.trim()) return;

    try {
      const updated = await NovelService.updateNovel(novelId, {
        title: editedTitle.trim()
      });
      setNovel(updated);
      setIsEditingTitle(false);
    } catch (err) {
      console.error('Error updating novel title:', err);
      setError('Failed to update novel title');
    }
  };

  const handleAddChapter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !novelId || !newChapterTitle.trim()) return;

    try {
      setError(null);
      const newChapter = await NovelService.createChapter(novelId, {
        title: newChapterTitle.trim(),
        user_id: user.id
      });

      // Reload novel to get updated chapters
      await loadNovel();
      setNewChapterTitle('');
      setShowAddChapter(false);

      // Navigate to the new chapter
      navigate(`/novel/${novelId}/chapter/${newChapter.chapter_id}`);
    } catch (err) {
      console.error('Error creating chapter:', err);
      setError('Failed to create chapter');
    }
  };

  const handleDeleteChapter = async (chapterId: string) => {
    if (!novelId || !window.confirm('Are you sure you want to delete this chapter?')) return;

    try {
      await NovelService.deleteChapter(novelId, chapterId);

      // Reload novel
      await loadNovel();
    } catch (err) {
      console.error('Error deleting chapter:', err);
      setError('Failed to delete chapter');
    }
  };

  const handleChapterClick = (chapterId: string) => {
    navigate(`/novel/${novelId}/chapter/${chapterId}`);
  };

  const handleEditChapter = (chapterId: string, currentTitle: string) => {
    setEditingChapterId(chapterId);
    setEditedChapterTitle(currentTitle);
    setActiveMenuChapterId(null);
  };

  const handleSaveChapterTitle = async (chapterId: string) => {
    if (!novelId || !editedChapterTitle.trim()) return;

    try {
      await NovelService.updateChapter(novelId, chapterId, {
        title: editedChapterTitle.trim()
      });

      // Reload novel to get updated chapters
      await loadNovel();
      setEditingChapterId(null);
      setEditedChapterTitle('');
    } catch (err) {
      console.error('Error updating chapter title:', err);
      setError('Failed to update chapter title');
    }
  };

  const handleCancelChapterEdit = () => {
    setEditingChapterId(null);
    setEditedChapterTitle('');
  };

  const handleEditMetadata = () => {
    navigate(`/novel/${novelId}/metadata`);
  };

  const handleMoveChapterUp = async (chapterId: string) => {
    if (!novel || !novelId || isReordering) return;

    const sortedChapters = [...novel.chapters].sort((a, b) => (a.order || 0) - (b.order || 0));
    const currentIndex = sortedChapters.findIndex(ch => ch.chapter_id === chapterId);

    if (currentIndex <= 0) return; // Already at the top

    try {
      setIsReordering(true);
      setError(null);

      // Create new order by swapping with previous chapter
      const newChapters = [...sortedChapters];
      [newChapters[currentIndex - 1], newChapters[currentIndex]] =
        [newChapters[currentIndex], newChapters[currentIndex - 1]];

      // Optimistically update local state
      const optimisticNovel = {
        ...novel,
        chapters: newChapters
      };
      setNovel(optimisticNovel);

      // Call API to persist the change
      await NovelService.reorderChapters(novelId, newChapters);

      // Reload to ensure consistency
      await loadNovel();
    } catch (err) {
      console.error('Error reordering chapters:', err);
      setError('Failed to reorder chapters');
      // Revert by reloading
      await loadNovel();
    } finally {
      setIsReordering(false);
    }
  };

  const handleMoveChapterDown = async (chapterId: string) => {
    if (!novel || !novelId || isReordering) return;

    const sortedChapters = [...novel.chapters].sort((a, b) => (a.order || 0) - (b.order || 0));
    const currentIndex = sortedChapters.findIndex(ch => ch.chapter_id === chapterId);

    if (currentIndex < 0 || currentIndex >= sortedChapters.length - 1) return; // Already at the bottom

    try {
      setIsReordering(true);
      setError(null);

      // Create new order by swapping with next chapter
      const newChapters = [...sortedChapters];
      [newChapters[currentIndex], newChapters[currentIndex + 1]] =
        [newChapters[currentIndex + 1], newChapters[currentIndex]];

      // Optimistically update local state
      const optimisticNovel = {
        ...novel,
        chapters: newChapters
      };
      setNovel(optimisticNovel);

      // Call API to persist the change
      await NovelService.reorderChapters(novelId, newChapters);

      // Reload to ensure consistency
      await loadNovel();
    } catch (err) {
      console.error('Error reordering chapters:', err);
      setError('Failed to reorder chapters');
      // Revert by reloading
      await loadNovel();
    } finally {
      setIsReordering(false);
    }
  };

  if (loading) {
    return (
      <div className="novel-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
          rightActions={
            <button
              className="novel-close-btn"
              onClick={() => navigate('/novel')}
            >
              ✕
            </button>
          }
        />
        <div className="novel-editor-loading">Loading novel...</div>
      </div>
    );
  }

  if (error || !novel) {
    return (
      <div className="novel-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
          rightActions={
            <button
              className="novel-close-btn"
              onClick={() => navigate('/novel')}
            >
              ✕
            </button>
          }
        />
        <div className="novel-editor-error">
          {error || 'Novel not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="novel-editor-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          <button
            className="novel-back-btn"
            onClick={() => navigate('/novel')}
          >
            ← Back
          </button>
        }
        centerContent={
          <div className="novel-editor-header">
            {isEditingTitle ? (
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onBlur={handleTitleSave}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleTitleSave();
                  }
                }}
                className="novel-title-edit-input"
                autoFocus
              />
            ) : (
              <span onClick={handleTitleEdit} className="novel-title-display">
                {novel.title}
              </span>
            )}
          </div>
        }
        rightActions={
          <div className="novel-editor-actions">
            <button
              className="novel-metadata-toggle"
              onClick={handleEditMetadata}
            >
              Edit Metadata
            </button>
          </div>
        }
      />

      <div className="novel-editor-content">
        <div className="novel-chapters-sidebar">
          <div className="chapters-sidebar-header">
            <h3>Chapters</h3>
            <div className="chapters-sidebar-header-actions">
              <div className="chapters-sort-toggle">
                <button
                  className={`chapters-sort-btn ${sortMode === 'timestamp' ? 'active' : ''}`}
                  onClick={() => setSortMode('timestamp')}
                  title="Sort by order"
                >
                  #
                </button>
                <button
                  className={`chapters-sort-btn ${sortMode === 'title' ? 'active' : ''}`}
                  onClick={() => setSortMode('title')}
                  title="Sort by title"
                >
                  A–Z
                </button>
              </div>
              <button
                className="add-chapter-button"
                onClick={() => setShowAddChapter(true)}
              >
                +
              </button>
            </div>
          </div>

          {showAddChapter && (
            <form onSubmit={handleAddChapter} className="add-chapter-form">
              <input
                type="text"
                value={newChapterTitle}
                onChange={(e) => setNewChapterTitle(e.target.value)}
                placeholder="Chapter title..."
                className="add-chapter-input"
                autoFocus
              />
              <div className="add-chapter-actions">
                <button type="submit" className="add-chapter-submit">
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddChapter(false);
                    setNewChapterTitle('');
                  }}
                  className="add-chapter-cancel"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="chapters-list">
            {novel.chapters.length === 0 ? (
              <div className="chapters-empty">
                No chapters yet. Add your first chapter!
              </div>
            ) : (
              (() => {
                const sortedChapters = sortMode === 'title'
                  ? [...novel.chapters].sort((a, b) => a.title.localeCompare(b.title))
                  : [...novel.chapters].sort((a, b) => (a.order || 0) - (b.order || 0));
                return sortedChapters.map((chapter, index) => (
                  <div
                    key={chapter.chapter_id}
                    className="chapter-item"
                  >
                    {editingChapterId === chapter.chapter_id ? (
                      <div className="chapter-edit-form">
                        <input
                          type="text"
                          value={editedChapterTitle}
                          onChange={(e) => setEditedChapterTitle(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveChapterTitle(chapter.chapter_id);
                            } else if (e.key === 'Escape') {
                              handleCancelChapterEdit();
                            }
                          }}
                          className="chapter-edit-input"
                          autoFocus
                        />
                        <div className="chapter-edit-actions">
                          <button
                            className="chapter-edit-save"
                            onClick={() => handleSaveChapterTitle(chapter.chapter_id)}
                          >
                            Save
                          </button>
                          <button
                            className="chapter-edit-cancel"
                            onClick={handleCancelChapterEdit}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div
                          className="chapter-item-content"
                          onClick={() => handleChapterClick(chapter.chapter_id)}
                        >
                          <span className="chapter-number">{index + 1}.</span>
                          <span className="chapter-title">{chapter.title}</span>
                        </div>
                        {sortMode === 'timestamp' && (
                          <div className="chapter-reorder-buttons">
                            <button
                              className="chapter-reorder-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveChapterUp(chapter.chapter_id);
                              }}
                              disabled={index === 0 || isReordering}
                              title="Move up"
                            >
                              ↑
                            </button>
                            <button
                              className="chapter-reorder-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveChapterDown(chapter.chapter_id);
                              }}
                              disabled={index === sortedChapters.length - 1 || isReordering}
                              title="Move down"
                            >
                              ↓
                            </button>
                          </div>
                        )}
                        <div className="chapter-menu-container">
                          <button
                            className="chapter-menu-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMenuChapterId(
                                activeMenuChapterId === chapter.chapter_id ? null : chapter.chapter_id
                              );
                            }}
                          >
                            ⋯
                          </button>
                          {activeMenuChapterId === chapter.chapter_id && (
                            <div className="chapter-menu-dropdown">
                              <button
                                className="chapter-menu-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditChapter(chapter.chapter_id, chapter.title);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="chapter-menu-item chapter-menu-item-delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveMenuChapterId(null);
                                  handleDeleteChapter(chapter.chapter_id);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ));
              })()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NovelEditor;

